import { Box, Typography, useMediaQuery } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReportDetailsInfo from './ReportDetailsInfo';
import { useLocation, useParams } from 'react-router-dom';
import { ReportCardResult, TestCompoundMetaDataSet, ViewOption } from '../types/reports';
import useSnackBarAlertMessages from '../hooks/useSnackBarAlertMessages';
import { calculateReportScores } from '../utils/reports';
import GradientCircularProgressContainer from '../components/common/GradientCircularProgressContainer';
import ResultTabContent from './ResultTabContent';
import YourNasemScaleWithGuidelines from './YourNasemScaleWithGuidelines';
import ResultTabNavigator from './ResultTabNavigator';
import SnackBarAlertMobileExperience from './SnackBarAlertMobileExperience';
import TestResultCardSlider from './details/TestResultCardsSlider';
import useReportAndCompoundMeta from '../hooks/useReportAndCompoundMeta';
import DownloadPDFReportResults from './details/DownloadPDFReportResults';
import { useThemeContext } from '../contexts/BaseThemeContextProvider';

const ReportDetails = () => {
  const { theme } = useThemeContext();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  // this data is injected into the state within the TestResultsHistory navigate function
  const reportDataFromState = location.state?.data;
  const { errorMessage, fullCompoundMetaData, reportsDataWithScores, loading } =
    useReportAndCompoundMeta(reportDataFromState);

  const { addAlertMessages, renderSnackBarAlertComponent } = useSnackBarAlertMessages();
  const [activeView, setActiveView] = useState<ViewOption>('comparison');
  const isSmallDevice = useMediaQuery(theme.breakpoints.down('md'));
  const contentRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(true);
  const [isResultDetailsExpanded, setIsResultDetailsExpanded] = useState(false);
  const [compoundMetaData, setCompoundMetaData] = useState<TestCompoundMetaDataSet>({});
  const [compoundMetaReady, setCompoundMetaReady] = useState(false);

  useEffect(() => {
    if (errorMessage) {
      addAlertMessages({ message: errorMessage, severityType: 'error' });
    }
  }, [errorMessage]);

  const reportData = useMemo(() => {
    if (!id || !reportsDataWithScores.length) return null;

    const index = reportsDataWithScores.findIndex((compound) => compound.centre_report_id === Number(id));

    return index !== -1 ? reportsDataWithScores[index] : null;
  }, [reportsDataWithScores, id]);

  // make sure to get the compoundMeta only after reportData is ready.
  useEffect(() => {
    if (!reportData?.test_id) return;

    const testIdStr = reportData.test_id.toString();
    const meta = fullCompoundMetaData[testIdStr];

    if (meta) {
      setCompoundMetaData(meta);
      setCompoundMetaReady(true);
    } else {
      setCompoundMetaData({});
      setCompoundMetaReady(false);
      addAlertMessages({
        message: 'Compound metadata not found for this test ID.',
        severityType: 'error',
      });
    }
  }, [reportData?.test_id, fullCompoundMetaData]);

  if (loading || reportsDataWithScores.length === 0 || !reportData || !compoundMetaReady) {
    return <GradientCircularProgressContainer loadingMessage="Loading test report details..." />;
  }

  const handleResultDetailsExpandToggle = () => {
    setIsResultDetailsExpanded((prev) => !prev);
  };

  // if users already visited the test results history page, we already
  // calculated those values for that same test report
  let reportTotalCompoundsDetected = reportData?.totalCompoundsDetected;
  let reportTotalPFASScore = reportData?.totalPFASScore;
  let reportNasemScore = reportData?.nasemScore;
  let reportNasemLevel = reportData?.nasemLevel;

  if (!reportTotalCompoundsDetected || !reportTotalPFASScore || !reportNasemScore || !reportNasemLevel) {
    let { totalCompoundsDetected, totalPFASScore, nasemScore, nasemLevel } = calculateReportScores(
      reportData?.compoundDataWithDdfv,
      compoundMetaData,
    );
    reportNasemScore = nasemScore;
    reportNasemLevel = nasemLevel;
    reportTotalCompoundsDetected = totalCompoundsDetected;
    reportTotalPFASScore = totalPFASScore;
  }

  const onClickCardResults = () => {
    setActiveView('table');
    contentRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const renderSnackBarAlertMobileExperience = () => {
    if (!isSmallDevice) return <></>;

    return <SnackBarAlertMobileExperience open={isOpen} onClose={() => setIsOpen(false)} />;
  };

  const renderResultCardsSlider = () => {
    const decoratedReportsDataWithScores: ReportCardResult[] = reportsDataWithScores.map((report) => ({
      centre_report_id: report.centre_report_id,
      report_date: report.report_date,
      sample_id: report.sample_id,
      totalCompoundsDetected: report.totalCompoundsDetected,
      totalPFASScore: report.totalPFASScore,
      nasemScore: report.nasemScore,
      nasemLevel: report.nasemLevel,
    }));
    return (
      <TestResultCardSlider
        onClickCardResults={onClickCardResults}
        activeReportDateCentreReportId={reportData.centre_report_id}
        reportsDataWithScores={decoratedReportsDataWithScores}
      />
    );
  };

  const renderHeaderDetails = () => {
    return (
      <Box
        sx={{
          background: theme.custom.report.details.header.backgroundColor,
          display: 'flex',
          gap: { xs: 0, smMd700: 3 },
          paddingLeft: '0px !important',
          mb: 2,
          flexDirection: { xs: 'column', smMd700: 'row' },
          paddingRight: '0px !important',
        }}
      >
        {/* Image shown only for desktop */}
        <Box
          component="img"
          sx={{
            objectFit: 'cover',
            objectPosition: 'left top',
            borderRadius: '16px',
            height: { smMd700: 221, md: 256 },
            width: { smMd700: 234, md: 300 },
            display: { xs: 'none', smMd700: 'block' },
            mt: 3,
            ml: 0,
          }}
          src={'/images/results/results-details-page-header-image.png'}
        />
        <Box
          display="flex"
          flexDirection="column"
          flexGrow="1"
          sx={{
            order: { xs: 1, smMd700: 2 },
          }}
        >
          <Box sx={{ mt: { xs: 3, md: 3 } }}>
            <Typography variant="h4" sx={{ color: theme.custom.report.details.header.color }}>
              Results
            </Typography>
            <Typography
              sx={{ color: theme.custom.report.details.subHeader.color, mb: 2, fontSize: '1.2rem', marginBottom: 0.5 }}
            >
              Review your results in detail
            </Typography>
          </Box>

          {/* Image shown only for mobile */}
          <Box
            component="img"
            sx={{
              objectFit: 'cover',
              objectPosition: 'left top',
              borderRadius: '16px',
              height: 203,
              width: '100%',
              display: { xs: 'block', smMd700: 'none' },
              ml: 0,
              mb: 2,
              mt: 0.5,
            }}
            src={'/images/results/results-details-page-header-image.png'}
          />
          {renderResultCardsSlider()}
        </Box>
      </Box>
    );
  };

  const reportDetailsInfoProps = {
    org_address: reportData.org_address,
    signing_doctor: reportData.signing_doctor,
    age: reportData.age,
    sample_date: reportData.sample_date,
    accession_date: reportData.accession_date,
    report_date: reportData.report_date,
    patient_name: reportData.patient_name,
    sample_id: reportData.sample_id,
    centre_report_id: reportData.centre_report_id,
    test_name: reportData.test_name,
    gender: reportData.gender,
  };

  return (
    <>
      {renderSnackBarAlertComponent()}
      {renderSnackBarAlertMobileExperience()}
      {renderHeaderDetails()}

      <Grid container spacing={1}>
        <Grid sx={{ width: '100%', marginTop: '24px', marginBottom: '24px' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 2,
              marginBottom: '16px',
              width: '100%',
              justifyContent: 'space-between',
              '@media (max-width: 400px)': {
                flexDirection: 'column',
                marginBottom: '0px',
              },
            }}
          >
            <ResultTabNavigator
              activeView={activeView}
              setActiveView={setActiveView}
              allResults={reportsDataWithScores}
            />
            <DownloadPDFReportResults
              reportData={{ test_name: reportData?.test_name, sample_id: reportData?.sample_id }}
            />
          </Box>

          <ResultTabContent
            reportDate={reportData.report_date}
            contentRef={contentRef}
            nasemScore={reportNasemScore}
            compoundDataList={reportData?.compoundDataWithDdfv}
            compoundMetaData={compoundMetaData}
            activeView={activeView}
            setActiveView={setActiveView}
            allResults={reportsDataWithScores}
          />
        </Grid>
        <Grid sx={{ width: '100%' }}>
          <YourNasemScaleWithGuidelines
            nasemLevel={reportNasemLevel.toLowerCase()}
            displayNasemLevel={true}
            onToggle={handleResultDetailsExpandToggle}
            isExpanded={isResultDetailsExpanded}
            testId={reportData?.test_id}
          />
        </Grid>
      </Grid>
      <ReportDetailsInfo reportData={reportDetailsInfoProps} isResultDetailsExpanded={isResultDetailsExpanded} />
    </>
  );
};

export default ReportDetails;
